import { ReduxAction } from "@thecb/components";
import { combineReducers } from "redux";
import { reducer as clients } from "../pages/clients/Clients.state";
import { reducer as agencies } from "../pages/agencies/Agencies.state";
import { reducer as locations } from "../pages/locations/Locations.state";
import { reducer as terminals } from "../pages/terminals/Terminals.state";
import { Alert } from "../types";
import { AlertVariants } from "../types/Alert";

/**
 * This combines all reducers for Cashiering Config tool into the
 * `posConfigTool` key in Revenue Management
 *
 * The state here is appropriate for generic/shared state such as alerts, toasts and error handling.
 */

interface State {
  alerts: Alert[];
}

const initialState: State = {
  alerts: [],
};

export const genericErrorMessage = (
  err: Error | unknown,
  heading = "Error"
): Partial<Alert> => ({
  heading: `${heading}:`,
  text: err instanceof Error ? err.message : "An error occurred",
  variant: AlertVariants.ERROR,
});

const ADD_POS_ALERT = "ADD_POS_ALERT";
export const addPOSAlert = (alert: Alert): ReduxAction<string, Alert> => ({
  type: ADD_POS_ALERT,
  payload: alert,
});

const DELETE_POS_ALERT = "DELETE_POS_ALERT";
export const deletePOSAlert = (
  index: number
): ReduxAction<string, { index: number }> => ({
  type: DELETE_POS_ALERT,
  payload: { index },
});

const CLEAR_POS_ALERTS = "CLEAR_POS_ALERTS";
export const clearPOSAlerts = (): ReduxAction => ({
  type: CLEAR_POS_ALERTS,
});

const REPLACE_POS_ALERTS = "REPLACE_POS_ALERTS";
export const replacePOSAlerts = (
  alert: Partial<Alert>
): ReduxAction<string, Partial<Alert>> => ({
  type: REPLACE_POS_ALERTS,
  payload: alert,
});

export const GENERIC_ERROR = "GENERIC_ERROR";
export const genericError = (
  message: string = "An error occurred"
): ReduxAction<string, { message: string }> => ({
  type: GENERIC_ERROR,
  payload: { message },
});

export const alerts = (
  state: State = initialState,
  { payload, type }: ReduxAction<string, Alert>
): State => {
  switch (type) {
    case ADD_POS_ALERT:
      return {
        ...state,
        alerts: [
          ...state.alerts,
          { ...payload, index: state.alerts.length + 1 } as Alert,
        ],
      };
    case DELETE_POS_ALERT:
      return {
        ...state,
        alerts: state.alerts.filter(
          (a) => state.alerts.indexOf(a) !== payload?.index
        ),
      };
    case CLEAR_POS_ALERTS:
      return { ...state, alerts: initialState.alerts };
    case REPLACE_POS_ALERTS:
      return { ...state, alerts: [{ ...payload, index: 0 } as Alert] };
    default:
      return state;
  }
};

const combinedReducer = combineReducers({
  alerts,
  clients,
  agencies,
  locations,
  terminals,
});

export default combinedReducer;
